import { useMemo, useState } from "react";
import { useOpsStore } from "../state/opsStore";
import type { OperatorLive } from "../state/opsStore";
import { useAuth } from "@/hooks/useAuth";
import { ComposeNoteDialog } from "@/features/directed-notes/components/ComposeNoteDialog";
import { canSendDirectNote } from "@/features/directed-notes/lib/permissions";
import "./OperatorGrid.css";

const IDLE_MS = 5 * 60 * 1000;

function isIdle(op: OperatorLive): boolean {
  if (op.currentDocId) return false;
  return Date.now() - op.activeSince > IDLE_MS;
}

interface OperatorGridProps {
  onOperatorClick?: (operatorId: string) => void;
}

/**
 * Phase 18 WU-18.D6 — Operator Grid (top-right of Ops Console).
 *
 * Renders a dense card per active operator with a green pulse indicator
 * (consistent with Phase 14 batch_session), current doc + KPM. Idle
 * operators (>5min no keystrokes, no current doc) fade the pulse to grey
 * and show "idle · " prefix in place of KPM.
 *
 * Row click → future operator detail drawer (wired by D7).
 */
export function OperatorGrid({ onOperatorClick }: OperatorGridProps) {
  const operatorsById = useOpsStore((s) => s.operators);
  const operators = useMemo(() => Object.values(operatorsById), [operatorsById]);
  // Task 13 (WU-2.3) — cap-gated "Send note" card action. Affordance only;
  // server is authoritative on supervisor.direct_note. Dialog state kept
  // local to the grid (smaller diff than hoisting to OpsConsolePage).
  const { user } = useAuth();
  const canDirectNote = canSendDirectNote(user);
  const [noteTarget, setNoteTarget] = useState<{ id: string; name: string | null } | null>(null);

  if (operators.length === 0) {
    return (
      <div
        className="ops-operator-grid__empty"
        data-testid="ops-operator-grid-empty"
      >
        No operators online.
      </div>
    );
  }

  return (
    <div className="ops-operator-grid" data-testid="ops-operator-grid">
      {noteTarget && (
        <ComposeNoteDialog
          presetRecipient={noteTarget}
          onClose={() => setNoteTarget(null)}
          onSent={() => setNoteTarget(null)}
        />
      )}
      {operators.map((op) => {
        const idle = isIdle(op);
        const cls = `ops-operator-card${idle ? " ops-operator-card--idle" : ""}`;
        return (
          <div
            key={op.operatorId}
            role="button"
            tabIndex={0}
            className={cls}
            data-operator-id={op.operatorId}
            onClick={() => onOperatorClick?.(op.operatorId)}
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget) return; // an inner control (Send note) owns its own keys
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOperatorClick?.(op.operatorId);
              }
            }}
          >
            <span className="ops-operator-card__pulse" aria-hidden />
            <span className="ops-operator-card__name" title={op.name}>
              {op.name}
            </span>
            <span className="ops-operator-card__kpm">
              {idle ? "—" : `${Math.round(op.kpm5m)} kpm`}
            </span>
            {canDirectNote && (
              <button
                type="button"
                className="ops-operator-card__note-btn"
                data-testid={`send-note-${op.operatorId}`}
                aria-label={`Send note to ${op.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setNoteTarget({ id: op.operatorId, name: op.name });
                }}
              >
                ✉
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
